/**
 * Tracks "Recommended Product Clicked" and "Recommended Product Viewed" events for Mixpanel
 * Listens for clicks on product links within recommendation sections
 * Tracks when recommended products become visible on screen
 */

(function () {
  'use strict';

  // Track which products have been viewed to avoid duplicate events
  const viewedProducts = new Set();

  /**
   * Checks if an element or any of its parents contain "algolia-recommend" in their classes
   * Returns the element that has the class, or null if not found
   */
  function findAlgoliaRecommendElement(element) {
    if (!element) return null;

    let currentElement = element;

    // Traverse up the DOM tree
    while (currentElement && currentElement !== document.body) {
      const classList = currentElement.className || '';
      if (typeof classList === 'string' && classList.includes('algolia-recommend')) {
        return currentElement;
      }
      // Also check classList if it's a DOMTokenList
      if (classList && typeof classList.contains === 'function' && classList.contains('algolia-recommend')) {
        return currentElement;
      }
      currentElement = currentElement.parentElement;
    }

    return null;
  }

  /**
   * Tries to get the Algolia hit object from the element
   */
  function getAlgoliaHitFromElement(element) {
    if (!element) return null;

    try {
      // Find the card wrapper element that has Algolia data attributes
      const cardElement = element.closest('[data-algolia-objectid]');
      if (!cardElement) return null;

      const objectID = cardElement.dataset.algoliaObjectid;
      if (!objectID) return null;

      // Try to get the hit from Algolia's results
      if (
        window.algoliaShopify &&
        window.algoliaShopify.instantsearch &&
        window.algoliaShopify.instantsearch.search &&
        window.algoliaShopify.instantsearch.search.helper
      ) {
        const helper = window.algoliaShopify.instantsearch.search.helper;
        const results = helper.lastResults;

        if (results && results.hits) {
          // Search for the hit by objectID
          const hit = results.hits.find((h) => h.objectID === objectID);
          if (hit) {
            return hit;
          }
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Extracts product data from an Algolia hit object
   */
  function extractProductDataFromHit(hit) {
    if (!hit) {
      return {
        product_id: null,
        variant_id: null,
        sku: null,
        position: null,
        parent_product_id: null,
        product_name: null,
        parent_product_name: null,
      };
    }

    return {
      product_id: hit.id || hit.objectID || null,
      variant_id: hit.objectID || hit.variant_id || null,
      sku: hit.sku || hit.meta?.sku?.value || null,
      position: hit.productPosition || hit.__position || hit.position || null,
      parent_product_id: null, // This might need to come from context
      product_name: hit.title || hit.product_title || null,
      handle: hit.handle || null, // canonical, language-stable product key
      parent_product_name: null, // Will be set from PDP
    };
  }

  /**
   * Extracts position from aria-label (format: "{position} of {total}")
   */
  function extractPositionFromAriaLabel(element) {
    if (!element) return null;

    // Find the closest <li> element with aria-label
    const listItem = element.closest('li[aria-label]');
    if (!listItem) return null;

    const ariaLabel = listItem.getAttribute('aria-label');
    if (!ariaLabel) return null;

    // Parse aria-label format: "1 of 5" or "1 {{ 'general.slider.of' | t }} 5"
    const match = ariaLabel.match(/(\d+)\s+(?:of|{{.*?}})\s+(\d+)/i);
    if (match && match[1]) {
      return parseInt(match[1], 10);
    }

    // Fallback: try to extract just the first number
    const firstNumber = ariaLabel.match(/\d+/);
    if (firstNumber) {
      return parseInt(firstNumber[0], 10);
    }

    return null;
  }

  /**
   * Gets variant ID from data-object-id or data-algolia-objectid attribute
   */
  function getVariantIdFromObjectId(element) {
    if (!element) return null;

    // First try data-algolia-objectid (used in Algolia recommendations)
    const algoliaObjectIdElement = element.closest('[data-algolia-objectid]');
    if (algoliaObjectIdElement) {
      const objectId = algoliaObjectIdElement.getAttribute('data-algolia-objectid');
      if (objectId) return objectId;
    }

    // Fallback to data-object-id
    const objectIdElement = element.closest('[data-object-id]');
    if (!objectIdElement) return null;

    const objectId = objectIdElement.getAttribute('data-object-id');
    return objectId || null;
  }

  /**
   * Gets product title from CTA/link text
   */
  function getProductTitleFromLink(element) {
    if (!element) return null;

    // Find the product link
    const link = element.closest('a[href*="/products/"]');
    if (!link) return null;

    // Try to get text from the link or its child elements
    // Look for card heading or link text
    const heading = link.querySelector('.card__heading, h3, h4, h5');
    if (heading) {
      // Get text content, but remove vendor name and finish name if present
      let title = heading.textContent.trim();
      // Remove vendor name span if present
      const vendorSpan = heading.querySelector('.vendor-name');
      if (vendorSpan) {
        title = title.replace(vendorSpan.textContent.trim(), '').trim();
      }
      // Remove finish name span if present
      const finishSpan = heading.querySelector('.meta-finish-name');
      if (finishSpan) {
        title = title.replace(finishSpan.textContent.trim(), '').trim();
      }
      return title || link.textContent.trim();
    }

    return link.textContent.trim() || null;
  }

  /**
   * Gets product handle from link href
   */
  function getProductHandleFromLink(element) {
    if (!element) return null;

    const link = element.closest('a[href*="/products/"]');
    if (!link) return null;

    const href = link.getAttribute('href');
    if (!href) return null;

    // Extract handle from URL like /products/handle or /products/handle?variant=123
    const match = href.match(/\/products\/([^\/\?]+)/);
    if (match && match[1]) {
      return match[1];
    }

    return null;
  }

  /**
   * Fetches product data using product handle
   */
  async function fetchProductDataByHandle(handle) {
    if (!handle) return null;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`);
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  /**
   * Gets parent product ID from current PDP
   */
  function getParentProductIdFromPDP() {
    // Try to get from product-info element (main product page)
    const productInfo = document.querySelector('product-info[data-product-id]');
    if (productInfo && productInfo.dataset.productId) {
      return productInfo.dataset.productId;
    }

    // Fallback: try to get from product JSON script tag
    const productDataScript = document.querySelector('script[id^="ProductJSON-"], script[id^="ProductData-"]');
    if (productDataScript) {
      try {
        const productData = JSON.parse(productDataScript.textContent);
        if (productData.product_id) {
          return productData.product_id.toString();
        }
      } catch (error) {
        // Error parsing product JSON
      }
    }

    return null;
  }

  /**
   * Gets parent product name from current PDP
   */
  function getParentProductNameFromPDP() {
    // Try to get from product JSON script tag
    const productDataScript = document.querySelector('script[id^="ProductJSON-"], script[id^="ProductData-"]');
    if (productDataScript) {
      try {
        const productData = JSON.parse(productDataScript.textContent);
        if (productData.product_title || productData.title) {
          return productData.product_title || productData.title;
        }
      } catch (error) {
        // Error parsing product JSON for name
      }
    }

    // Fallback: try to get from product-info element's h1 or product title
    const productInfo = document.querySelector('product-info');
    if (productInfo) {
      const productTitle = productInfo.querySelector('h1, [data-product-title], .product__title');
      if (productTitle) {
        return productTitle.textContent.trim();
      }
    }

    // Fallback: try to get from page title or meta tags
    const metaTitle = document.querySelector('meta[property="og:title"]');
    if (metaTitle && metaTitle.content) {
      return metaTitle.content;
    }

    return null;
  }

  /**
   * Gets parent product handle (canonical, language-stable) from current PDP
   */
  function getParentProductHandleFromPDP() {
    // Prefer the PDP ProductData JSON (carries the canonical handle)
    const productDataScript = document.querySelector('script[id^="ProductJSON-"], script[id^="ProductData-"]');
    if (productDataScript) {
      try {
        const productData = JSON.parse(productDataScript.textContent);
        if (productData.product_handle) {
          return productData.product_handle;
        }
      } catch (error) {
        // Error parsing product JSON for handle
      }
    }

    // Fallback: parse from the current URL (/products/<handle>), locale prefix tolerant
    const match = window.location.pathname.match(/\/products\/([^\/\?]+)/);
    return match && match[1] ? match[1] : null;
  }

  /**
   * Extracts product data from HTML structure (new method)
   */
  async function extractProductDataFromHTML(element) {
    if (!element) {
      return {
        product_id: null,
        variant_id: null,
        sku: null,
        position: null,
        parent_product_id: null,
        product_name: null,
        parent_product_name: null,
      };
    }

    // Get variant ID from data-object-id
    const variantId = getVariantIdFromObjectId(element);

    // Get position from aria-label
    const position = extractPositionFromAriaLabel(element);

    // Get product title from link text
    const productTitle = getProductTitleFromLink(element);

    // Get product handle from link href
    const productHandle = getProductHandleFromLink(element);

    // Get parent product ID from current PDP
    const parentProductId = getParentProductIdFromPDP();

    // Get parent product name from current PDP
    const parentProductName = getParentProductNameFromPDP();

    // Get parent product handle (canonical) from current PDP
    const parentProductHandle = getParentProductHandleFromPDP();

    // If we have handle and variant ID, fetch full product data
    let productData = {
      product_id: null,
      variant_id: variantId || null, // Ensure variant ID is set from data-object-id
      sku: null,
      position: position,
      parent_product_id: parentProductId,
      product_name: productTitle, // Recommended product name (localized — display)
      product_handle: productHandle, // canonical, language-stable product key
      parent_product_name: parentProductName, // Parent product name (localized — display)
      parent_product_handle: parentProductHandle, // canonical, language-stable parent key
    };

    if (productHandle) {
      const fullProductData = await fetchProductDataByHandle(productHandle);
      if (fullProductData) {
        productData.product_id = fullProductData.id?.toString() || null;

        // Use product title from fetched data if we don't have it from link
        if (!productData.product_name && (fullProductData.title || fullProductData.product_title)) {
          productData.product_name = fullProductData.title || fullProductData.product_title;
        }

        // Find the variant by variant ID
        if (variantId && fullProductData.variants) {
          const variant = fullProductData.variants.find((v) => v.id.toString() === variantId.toString());
          if (variant) {
            productData.sku = variant.sku || null;
            // If we didn't get product_id from the product, try to get it from variant
            if (!productData.product_id && variant.product_id) {
              productData.product_id = variant.product_id.toString();
            }
          }
        }
      }
    }

    return productData;
  }

  /**
   * Extracts product data from an element and its parents (fallback method)
   */
  function extractProductDataFromElement(element) {
    if (!element) {
      return {
        product_id: null,
        variant_id: null,
        sku: null,
        position: null,
        parent_product_id: null,
        product_name: null,
        parent_product_name: null,
      };
    }

    let currentElement = element;
    const productData = {
      product_id: null,
      variant_id: null,
      sku: null,
      position: null,
      parent_product_id: null,
      product_name: null,
      parent_product_name: null,
    };

    // Traverse up the DOM to find product data
    while (currentElement && currentElement !== document.body) {
      // Check for product data attributes
      if (currentElement.dataset) {
        if (currentElement.dataset.productId && !productData.product_id) {
          productData.product_id = currentElement.dataset.productId;
        }
        if (currentElement.dataset.variantId && !productData.variant_id) {
          productData.variant_id = currentElement.dataset.variantId;
        }
        if (currentElement.dataset.sku && !productData.sku) {
          productData.sku = currentElement.dataset.sku;
        }
        if (currentElement.dataset.position && !productData.position) {
          productData.position = currentElement.dataset.position;
        }
        // Check for Algolia-specific data attributes
        if (currentElement.dataset.algoliaPosition && !productData.position) {
          productData.position = currentElement.dataset.algoliaPosition;
        }
        if (currentElement.dataset.parentProductId && !productData.parent_product_id) {
          productData.parent_product_id = currentElement.dataset.parentProductId;
        }
      }

      // Try to get position from element index if not found
      if (!productData.position && currentElement.parentElement) {
        const siblings = Array.from(currentElement.parentElement.children);
        const index = siblings.indexOf(currentElement);
        if (index !== -1) {
          productData.position = (index + 1).toString(); // 1-indexed
        }
      }

      // Try to get parent product ID from product-recommendations element
      if (!productData.parent_product_id) {
        const productRecommendationsElement = currentElement.closest('product-recommendations');
        if (productRecommendationsElement?.dataset?.productId) {
          productData.parent_product_id = productRecommendationsElement.dataset.productId;
        }
      }

      currentElement = currentElement.parentElement;
    }

    return productData;
  }

  /**
   * Tracks recommended product clicks
   */
  async function trackRecommendedProductClick(event) {
    // Only track if the click is on a product link
    const link = event.target.closest('a[href*="/products/"]');
    if (!link) {
      return;
    }

    // Check if the target or its parents contain "algolia-recommend" in classes
    const algoliaRecommendElement = findAlgoliaRecommendElement(event.target);
    const isAlgoliaRecommend = algoliaRecommendElement !== null;

    let productData = {
      product_id: null,
      variant_id: null,
      sku: null,
      position: null,
      parent_product_id: null,
      product_name: null,
      parent_product_name: null,
    };

    if (isAlgoliaRecommend) {
      // Use new HTML-based extraction method
      productData = await extractProductDataFromHTML(event.target);

      // If HTML extraction didn't work, try Algolia hit object as fallback
      if (!productData.variant_id || !productData.position) {
        const algoliaHit = getAlgoliaHitFromElement(algoliaRecommendElement);
        if (algoliaHit) {
          const hitData = extractProductDataFromHit(algoliaHit);
          // Merge data, preferring HTML extraction results
          productData = {
            product_id: productData.product_id || hitData.product_id,
            variant_id: productData.variant_id || hitData.variant_id,
            sku: productData.sku || hitData.sku,
            position: productData.position || hitData.position,
            parent_product_id: productData.parent_product_id || hitData.parent_product_id,
            product_name: productData.product_name || hitData.product_name || null,
            product_handle: productData.product_handle || hitData.handle || null,
            parent_product_name: productData.parent_product_name || getParentProductNameFromPDP() || null,
            parent_product_handle: productData.parent_product_handle || getParentProductHandleFromPDP() || null,
          };
        }
      }
    } else {
      // Fallback to old method: find the closest element with recommended product data
      const recommendedProductElement = event.target.closest('[data-recommended-product]');

      if (!recommendedProductElement) {
        return;
      }

      // Try HTML extraction first
      productData = await extractProductDataFromHTML(event.target);

      // Fallback to data attributes if HTML extraction didn't work
      if (!productData.variant_id || !productData.position) {
        productData.product_id = productData.product_id || recommendedProductElement.dataset.productId;
        productData.variant_id = productData.variant_id || recommendedProductElement.dataset.variantId;
        productData.sku = productData.sku || recommendedProductElement.dataset.sku;
        productData.position = productData.position || recommendedProductElement.dataset.position;

        // Get parent product ID from the product-recommendations element
        const productRecommendationsElement = recommendedProductElement.closest('product-recommendations');
        productData.parent_product_id =
          productData.parent_product_id ||
          productRecommendationsElement?.dataset.productId ||
          recommendedProductElement.dataset.parentProductId;

        // Get product name from link if not already set
        if (!productData.product_name) {
          productData.product_name = getProductTitleFromLink(event.target);
        }

        // Get product handle (canonical) from link if not already set
        if (!productData.product_handle) {
          productData.product_handle = getProductHandleFromLink(event.target);
        }

        // Get parent product name if not already set
        if (!productData.parent_product_name) {
          productData.parent_product_name = getParentProductNameFromPDP();
        }

        // Get parent product handle (canonical) if not already set
        if (!productData.parent_product_handle) {
          productData.parent_product_handle = getParentProductHandleFromPDP();
        }
      }
    }

    // Validate required data
    if (
      !productData.product_id ||
      !productData.variant_id ||
      !productData.sku ||
      !productData.position ||
      !productData.parent_product_id
    ) {
      return;
    }

    try {
      // Check if Mixpanel is available
      if (typeof window.mixpanel === 'undefined' || !window.mixpanel.track) {
        return;
      }

      // Skip tracking for bots
      if (typeof isBot === 'function' && isBot()) {
        return;
      }

      // Track the event
      mixpanel.track('Recommended Product Clicked V.1', {
        'Product ID': productData.product_id,
        'Variant ID': productData.variant_id,
        SKU: productData.sku,
        Position: parseInt(productData.position, 10),
        'Parent Product ID': productData.parent_product_id,
        // Canonical, language-stable keys for cross-language grouping; names kept for display.
        'Product Handle': productData.product_handle || '',
        'Parent Product Handle': productData.parent_product_handle || '',
        'Product Name': productData.product_name || '',
        'Parent Product Name': productData.parent_product_name || '',
        'User Agent': navigator.userAgent,
      });
    } catch (error) {
      // Error tracking Recommended Product Clicked
    }
  }

  /**
   * Tracks "Recommended Product Viewed" event when a product becomes visible
   */
  async function trackRecommendedProductViewed(element) {
    if (!element) return;

    // Get variant ID from data-object-id to create unique identifier
    const variantId = getVariantIdFromObjectId(element);
    if (!variantId) {
      return;
    }

    // Check if we've already tracked this product view
    const viewKey = `viewed_${variantId}`;
    if (viewedProducts.has(viewKey)) {
      return;
    }

    // Find the product link within the element (same as click tracking)
    const link = element.querySelector('a[href*="/products/"]') || element.closest('a[href*="/products/"]');
    if (!link) {
      // Try to find link in parent elements
      const parentLink = element.closest('a[href*="/products/"]');
      if (!parentLink) {
        return;
      }
    }

    // Use the same extraction logic as click tracking
    // Find the algolia-recommend element or use the element itself
    const algoliaRecommendElement = findAlgoliaRecommendElement(element) || element;
    const isAlgoliaRecommend = algoliaRecommendElement !== null;

    let productData = {
      product_id: null,
      variant_id: null,
      sku: null,
      position: null,
      parent_product_id: null,
      product_name: null,
      parent_product_name: null,
    };

    if (isAlgoliaRecommend) {
      // Use new HTML-based extraction method (same as click tracking)
      // Pass the link or element - extractProductDataFromHTML will find the link
      const targetElement = link || element;
      productData = await extractProductDataFromHTML(targetElement);

      // If HTML extraction didn't work, try Algolia hit object as fallback
      if (!productData.variant_id || !productData.position) {
        const algoliaHit = getAlgoliaHitFromElement(algoliaRecommendElement);
        if (algoliaHit) {
          const hitData = extractProductDataFromHit(algoliaHit);
          // Merge data, preferring HTML extraction results
          productData = {
            product_id: productData.product_id || hitData.product_id,
            variant_id: productData.variant_id || hitData.variant_id,
            sku: productData.sku || hitData.sku,
            position: productData.position || hitData.position,
            parent_product_id: productData.parent_product_id || hitData.parent_product_id,
            product_name: productData.product_name || hitData.product_name || null,
            product_handle: productData.product_handle || hitData.handle || null,
            parent_product_name: productData.parent_product_name || getParentProductNameFromPDP() || null,
            parent_product_handle: productData.parent_product_handle || getParentProductHandleFromPDP() || null,
          };
        }
      }
    } else {
      // Fallback to old method: find the closest element with recommended product data
      const recommendedProductElement = element.closest('[data-recommended-product]');

      if (!recommendedProductElement) {
        // Still try HTML extraction on the element itself
        productData = await extractProductDataFromHTML(link || element);
      } else {
        // Try HTML extraction first (same as click tracking)
        const targetElement = link || element;
        productData = await extractProductDataFromHTML(targetElement);

        // Fallback to data attributes if HTML extraction didn't work
        if (!productData.variant_id || !productData.position) {
          productData.product_id = productData.product_id || recommendedProductElement.dataset.productId;
          productData.variant_id = productData.variant_id || recommendedProductElement.dataset.variantId;
          productData.sku = productData.sku || recommendedProductElement.dataset.sku;
          productData.position = productData.position || recommendedProductElement.dataset.position;

          // Get parent product ID from the product-recommendations element
          const productRecommendationsElement = recommendedProductElement.closest('product-recommendations');
          productData.parent_product_id =
            productData.parent_product_id ||
            productRecommendationsElement?.dataset.productId ||
            recommendedProductElement.dataset.parentProductId;

          // Get product name from link if not already set
          if (!productData.product_name) {
            productData.product_name = getProductTitleFromLink(link || element);
          }

          // Get product handle (canonical) from link if not already set
          if (!productData.product_handle) {
            productData.product_handle = getProductHandleFromLink(link || element);
          }

          // Get parent product name if not already set
          if (!productData.parent_product_name) {
            productData.parent_product_name = getParentProductNameFromPDP();
          }

          // Get parent product handle (canonical) if not already set
          if (!productData.parent_product_handle) {
            productData.parent_product_handle = getParentProductHandleFromPDP();
          }
        }
      }
    }

    // Ensure variant ID is set (from the element we're observing)
    if (!productData.variant_id && variantId) {
      productData.variant_id = variantId;
    }

    // Validate required data
    if (
      !productData.product_id ||
      !productData.variant_id ||
      !productData.sku ||
      !productData.position ||
      !productData.parent_product_id
    ) {
      return;
    }

    try {
      // Check if Mixpanel is available
      if (typeof window.mixpanel === 'undefined' || !window.mixpanel.track) {
        return;
      }

      // Skip tracking for bots
      if (typeof isBot === 'function' && isBot()) {
        return;
      }

      // Track the event
      mixpanel.track('Recommended Product Viewed V.1', {
        'Product ID': productData.product_id,
        'Variant ID': productData.variant_id,
        SKU: productData.sku,
        Position: parseInt(productData.position, 10),
        'Parent Product ID': productData.parent_product_id,
        // Canonical, language-stable keys for cross-language grouping; names kept for display.
        'Product Handle': productData.product_handle || '',
        'Parent Product Handle': productData.parent_product_handle || '',
        'Product Name': productData.product_name || '',
        'Parent Product Name': productData.parent_product_name || '',
        'User Agent': navigator.userAgent,
      });

      // Mark this product as viewed
      viewedProducts.add(viewKey);
    } catch (error) {
      // Error tracking Recommended Product Viewed
    }
  }

  /**
   * Checks if an element is actually visible (not hidden by CSS or carousel)
   */
  function isElementActuallyVisible(element) {
    if (!element) return false;

    // Check if element or any parent is hidden
    let current = element;
    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      if (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.opacity === '0' ||
        current.hasAttribute('hidden')
      ) {
        return false;
      }
      current = current.parentElement;
    }

    // Check if element is in viewport and has dimensions
    const rect = element.getBoundingClientRect();
    const isInViewport =
      rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;

    // Check if element has actual size (not collapsed)
    const hasSize = rect.width > 0 && rect.height > 0;

    return isInViewport && hasSize;
  }

  /**
   * Sets up Intersection Observer to track when recommended products become visible
   */
  function initRecommendedProductViewTracking() {
    // Check if Intersection Observer is supported
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    // Create observer with options
    const observerOptions = {
      root: null, // Use viewport as root
      rootMargin: '0px', // No margin - only track when actually in viewport
      threshold: 0.25, // Trigger when 25% of the element is visible (more reliable than 10%)
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // Only track if element is intersecting AND actually visible
        if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
          const element = entry.target;

          // Double-check that element is actually visible (not hidden by carousel)
          if (isElementActuallyVisible(element)) {
            trackRecommendedProductViewed(element);
            // Unobserve after tracking to avoid duplicate events
            observer.unobserve(element);
          }
        }
      });
    }, observerOptions);

    // Function to observe all recommended product elements
    const observeRecommendedProducts = () => {
      // Find all elements with data-algolia-objectid OR data-object-id
      const recommendedProducts = document.querySelectorAll('[data-algolia-objectid], [data-object-id]');

      recommendedProducts.forEach((element, index) => {
        // Only observe if it hasn't been viewed yet
        const variantId = getVariantIdFromObjectId(element);
        if (variantId) {
          const viewKey = `viewed_${variantId}`;
          if (!viewedProducts.has(viewKey)) {
            // Check if element is actually visible (not hidden by carousel)
            if (isElementActuallyVisible(element)) {
              // Element is already visible, track it immediately
              trackRecommendedProductViewed(element);
            } else {
              // Element not yet visible or hidden by carousel, observe it
              // It will only fire when it becomes actually visible
              observer.observe(element);
            }
          }
        }
      });
    };

    // Observe products on initial load (with a small delay to ensure DOM is ready)
    setTimeout(() => {
      observeRecommendedProducts();
    }, 500);

    // Also observe when new recommendations are loaded dynamically
    // Use MutationObserver to watch for new elements being added
    const mutationObserver = new MutationObserver((mutations) => {
      let shouldCheck = false;
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          // Check if any added nodes have the attributes we're looking for
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              // Element node
              if (
                node.hasAttribute('data-algolia-objectid') ||
                node.hasAttribute('data-object-id') ||
                node.querySelector('[data-algolia-objectid], [data-object-id]')
              ) {
                shouldCheck = true;
              }
            }
          });
        }
      });

      if (shouldCheck) {
        // Debounce to avoid too many checks
        clearTimeout(mutationObserver.debounceTimer);
        mutationObserver.debounceTimer = setTimeout(() => {
          observeRecommendedProducts();
        }, 300);
      }
    });

    // Observe the document body for changes
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Initialize tracking for recommended products
   */
  function initRecommendedProductTracking() {
    // Use event delegation to handle clicks on recommended products
    // This works for dynamically loaded recommendations too
    // Listen for both regular clicks and middle clicks (auxclick)
    document.addEventListener('click', trackRecommendedProductClick, true);
    document.addEventListener('auxclick', trackRecommendedProductClick, true);

    // Initialize view tracking
    initRecommendedProductViewTracking();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRecommendedProductTracking);
  } else {
    initRecommendedProductTracking();
  }

  // Also re-initialize when recommendations are loaded dynamically
  window.addEventListener('recommendationsLoaded', function () {
    // Tracking is already set up via event delegation, no need to re-initialize
  });
})();
